/**
 * Settings tab for the per-salon Off-Platform Payments catalog.
 *
 * Backend: bookings-infra PR #344 — table `salon_payment_methods`, endpoints
 * under `/business-locations/{salonId}/payment-methods`. Defaults (Check, Venmo, CashApp,
 * Zelle) are seeded with `is_default=true, is_selected=true` and can be
 * deselected here but never deleted or renamed. Salons add custom rows
 * (≤100 chars, case-insensitive-unique per salon) and pick up to 8 to expose
 * on the checkout grid via `is_selected`.
 *
 * View/edit pattern: clicking Edit clones server state into a local draft.
 * Add / delete / toggle all stage into the draft. Save commits the diff to
 * the backend in this order: POST adds → DELETE deletes → PUT selection.
 * Cancel discards the draft and returns to view.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import {
  MAX_SELECTED_PAYMENT_METHODS,
  PAYMENT_METHOD_NAME_MAX_LENGTH,
  SalonPaymentMethod,
} from "../../types/salonPaymentMethods";

interface OffPlatformPaymentsSettingsProps {
  readOnly?: boolean;
}

// In-memory shape used in edit mode. Server rows carry their real UUID;
// pending adds carry a synthetic `temp:<n>` id so the React key stays
// stable across toggle / re-render until Save mints a real ID.
interface DraftRow {
  id: string;
  name: string;
  isDefault: boolean;
  isSelected: boolean;
  isPendingAdd: boolean;
}

let _draftIdCounter = 0;
const nextDraftId = (): string => `temp:${++_draftIdCounter}`;

const AFFIRM_STRIPE_PREREQ_MESSAGE =
  "Please make sure Affirm is enabled on your Stripe account first.";

const AFFIRM_TOOLTIP_MESSAGE =
  "Offer Affirm financing at in-store checkout via Stripe WisePOS E terminal.";

/**
 * Visible ⓘ affordance for the Affirm financing tooltip — the text-hover
 * tooltip alone isn't discoverable. ActionIcon gives a focusable,
 * touch-friendly target; the tooltip also opens on focus and tap so it
 * works on mobile, where hover doesn't exist.
 */
const AffirmInfoIcon: React.FC = () => (
  <Tooltip
    label={AFFIRM_TOOLTIP_MESSAGE}
    withArrow
    events={{ hover: true, focus: true, touch: true }}
  >
    <ActionIcon
      variant="subtle"
      color="gray"
      size="xs"
      aria-label="About Affirm"
    >
      <IconInfoCircle size={14} />
    </ActionIcon>
  </Tooltip>
);

const OffPlatformPaymentsSettings: React.FC<
  OffPlatformPaymentsSettingsProps
> = ({ readOnly = false }) => {
  const { businessInfo, setBusinessInfo } = useSalon() as any;
  const salonId = businessInfo?.id as string | undefined;
  const {
    getSalonPaymentMethods,
    addSalonPaymentMethod,
    deleteSalonPaymentMethod,
    setSalonPaymentMethodsSelection,
    updateSalon,
  } = useApp();

  const [serverMethods, setServerMethods] = useState<SalonPaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<"view" | "edit">("view");
  const [draftRows, setDraftRows] = useState<DraftRow[]>([]);
  const [draftName, setDraftName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Affirm (BNPL) business opt-in. Not part of the catalog: it lives on the
  // salon record (PUT /business-locations) and is only actionable once Stripe reports
  // the affirm_payments capability active on the connected account.
  const affirmCapable =
    businessInfo?.stripe_affirm_capability_status === "active";
  const affirmEnabled = Boolean(businessInfo?.affirm_payments_enabled);
  const [draftAffirmEnabled, setDraftAffirmEnabled] = useState(false);

  // -- Initial fetch -------------------------------------------------------
  useEffect(() => {
    if (!salonId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const list = await getSalonPaymentMethods(salonId);
        if (!cancelled) setServerMethods(list ?? []);
      } catch (err) {
        console.error("Failed to load off-platform payment methods", err);
        if (!cancelled) setError("Couldn't load off-platform payment methods.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonId, getSalonPaymentMethods]);

  // -- Derived rendering source -------------------------------------------
  // In view mode we render the server state directly. In edit mode we
  // render the local draft.
  const viewRows: DraftRow[] = useMemo(
    () =>
      serverMethods.map((m) => ({
        id: m.id,
        name: m.name,
        isDefault: m.is_default,
        isSelected: m.is_selected,
        isPendingAdd: false,
      })),
    [serverMethods],
  );
  const rows = mode === "edit" ? draftRows : viewRows;
  const selectedCount = rows.filter((r) => r.isSelected).length;

  const trimmedDraft = draftName.trim();
  const draftDuplicate = rows.some(
    (r) => r.name.toLowerCase() === trimmedDraft.toLowerCase(),
  );
  const canAdd =
    mode === "edit" &&
    !isSaving &&
    trimmedDraft.length > 0 &&
    trimmedDraft.length <= PAYMENT_METHOD_NAME_MAX_LENGTH &&
    !draftDuplicate;

  // -- Mode transitions ---------------------------------------------------
  const handleEdit = () => {
    setDraftRows(viewRows);
    setDraftName("");
    setDraftAffirmEnabled(affirmEnabled);
    setError(null);
    setMode("edit");
  };

  const handleCancel = () => {
    setMode("view");
    setDraftRows([]);
    setDraftName("");
    setDraftAffirmEnabled(affirmEnabled);
    setError(null);
  };

  // -- Draft mutations (no network) ---------------------------------------
  const toggleDraftRow = (id: string) => {
    setDraftRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const turningOn = !r.isSelected;
        if (
          turningOn &&
          prev.filter((x) => x.isSelected).length >=
            MAX_SELECTED_PAYMENT_METHODS
        ) {
          return r;
        }
        return { ...r, isSelected: turningOn };
      }),
    );
  };

  const deleteDraftRow = (id: string) => {
    setDraftRows((prev) => prev.filter((r) => r.id !== id));
  };

  const addDraftRow = () => {
    if (!canAdd) return;
    setDraftRows((prev) => [
      ...prev,
      {
        id: nextDraftId(),
        name: trimmedDraft,
        isDefault: false,
        // Auto-select if cap allows. Otherwise add unchecked and let
        // the user free up a slot.
        isSelected:
          prev.filter((r) => r.isSelected).length <
          MAX_SELECTED_PAYMENT_METHODS,
        isPendingAdd: true,
      },
    ]);
    setDraftName("");
  };

  // -- Save (apply diff to backend) ---------------------------------------
  const handleSave = async () => {
    if (!salonId) return;
    setIsSaving(true);
    setError(null);
    try {
      // 1. Adds: POST each pending-add row in draft order. After each
      //    POST the response carries the full catalog; keep the latest
      //    snapshot so we can map temp ids → real ids.
      let latestCatalog: SalonPaymentMethod[] = serverMethods;
      const tempIdToRealId = new Map<string, string>();
      const pendingAdds = draftRows.filter((r) => r.isPendingAdd);
      for (const add of pendingAdds) {
        const response = await addSalonPaymentMethod(salonId, add.name);
        latestCatalog = response?.payment_methods ?? latestCatalog;
        // Match by case-insensitive name — backend enforces uniqueness so
        // exactly one row matches.
        const matched = latestCatalog.find(
          (m) => m.name.toLowerCase() === add.name.toLowerCase(),
        );
        if (matched) tempIdToRealId.set(add.id, matched.id);
      }

      // 2. Deletes: any server row that's not present in the draft and
      //    isn't a default. (Defaults can't be deleted.)
      const draftIds = new Set(draftRows.map((r) => r.id));
      const toDelete = serverMethods.filter(
        (m) => !m.is_default && !draftIds.has(m.id),
      );
      for (const m of toDelete) {
        const response = await deleteSalonPaymentMethod(salonId, m.id);
        latestCatalog = response?.payment_methods ?? latestCatalog;
      }

      // 3. Selection: PUT the final selected-id set (real ids only).
      const finalSelectedIds = draftRows
        .filter((r) => r.isSelected)
        .map((r) =>
          r.isPendingAdd ? (tempIdToRealId.get(r.id) ?? null) : r.id,
        )
        .filter((id): id is string => !!id);
      const selectionResponse = await setSalonPaymentMethodsSelection(
        salonId,
        finalSelectedIds,
      );
      latestCatalog = selectionResponse?.payment_methods ?? latestCatalog;

      // 4. Affirm (BNPL) opt-in: lives on the salon record, not the catalog.
      //    Persist via PUT /business-locations and update SalonContext optimistically
      //    (the standard settings pattern — updateSalon has no auto-refetch).
      if (affirmCapable && draftAffirmEnabled !== affirmEnabled) {
        // Send ONLY the field this save changes. PUT /business-locations writes every
        // recognized key it receives, so spreading the whole businessInfo
        // record replays potentially-stale values over the DB row.
        await updateSalon({
          id: salonId,
          affirm_payments_enabled: draftAffirmEnabled,
        });
        setBusinessInfo((prev: any) => ({
          ...prev,
          affirm_payments_enabled: draftAffirmEnabled,
        }));
      }

      setServerMethods(latestCatalog);
      setDraftRows([]);
      setDraftName("");
      setMode("view");
    } catch (err: any) {
      console.error("Failed to save off-platform payment methods", err);
      setError(
        err?.message ??
          "Couldn't save your changes. Please review the list and try again.",
      );
      // Resync from server so the user can see authoritative state +
      // retry without stale ids.
      try {
        const list = await getSalonPaymentMethods(salonId);
        setServerMethods(list ?? []);
        // Stay in edit mode so the user can fix and re-Save.
        setDraftRows(
          (list ?? []).map((m) => ({
            id: m.id,
            name: m.name,
            isDefault: m.is_default,
            isSelected: m.is_selected,
            isPendingAdd: false,
          })),
        );
      } catch {
        // outer error already surfaced
      }
    } finally {
      setIsSaving(false);
    }
  };

  // -- Render --------------------------------------------------------------
  return (
    <Paper p="md" radius="md" withBorder pos="relative">
      <LoadingOverlay visible={isLoading || isSaving} />
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Title order={4}>Off-Platform Payments</Title>
          {!readOnly && mode === "view" && (
            <Button
              leftSection={<IconPencil size={14} />}
              variant="default"
              size="xs"
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
          {!readOnly && mode === "edit" && (
            <Group gap="xs" wrap="nowrap">
              <Button
                variant="subtle"
                size="xs"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="var(--brand-teal)"
                onClick={handleSave}
                loading={isSaving}
              >
                Save
              </Button>
            </Group>
          )}
        </Group>

        <Text size="sm" c="dimmed">
          Pick up to {MAX_SELECTED_PAYMENT_METHODS} methods to show on the
          checkout grid (in addition to Cash and Card). Defaults can be
          deselected but not deleted; add your own to suit your business.
        </Text>

        {error && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Stack gap={6}>
          {rows.length === 0 && !isLoading && (
            <Text size="sm" c="dimmed">
              No off-platform payment methods yet.
            </Text>
          )}
          {rows.map((row) =>
            mode === "view" ? (
              <ViewRow key={row.id} row={row} />
            ) : (
              <EditRow
                key={row.id}
                row={row}
                disabled={isSaving}
                disableToggleOn={
                  !row.isSelected &&
                  selectedCount >= MAX_SELECTED_PAYMENT_METHODS
                }
                onToggle={() => toggleDraftRow(row.id)}
                onDelete={() => deleteDraftRow(row.id)}
              />
            ),
          )}
        </Stack>

        <Text size="xs" c="dimmed">
          {selectedCount} of {MAX_SELECTED_PAYMENT_METHODS} selected
        </Text>

        {mode === "edit" && (
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              placeholder="Add a method..."
              value={draftName}
              onChange={(event) => setDraftName(event.currentTarget.value)}
              maxLength={PAYMENT_METHOD_NAME_MAX_LENGTH}
              error={
                trimmedDraft.length > 0 && draftDuplicate
                  ? "That name already exists"
                  : undefined
              }
              disabled={isSaving}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canAdd) {
                  event.preventDefault();
                  addDraftRow();
                }
              }}
            />
            <Button onClick={addDraftRow} disabled={!canAdd} variant="default">
              Add
            </Button>
          </Group>
        )}

        {/* Affirm (BNPL) opt-in — a salon-record setting, not a catalog
            method: processed through the Stripe terminal and exempt from
            the selected-methods cap. Toggleable only once Stripe reports
            the affirm_payments capability active. */}
        <Divider
          label={
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">
              Buy Now Pay Later
            </Text>
          }
          labelPosition="center"
        />
        {mode === "view" ? (
          <Group gap="xs" wrap="nowrap">
            <Tooltip label={AFFIRM_TOOLTIP_MESSAGE} withArrow>
              <Group gap="xs" wrap="nowrap">
                {affirmEnabled && affirmCapable ? (
                  <IconCheck size={16} color="var(--brand-teal)" />
                ) : (
                  <Box w={16} />
                )}
                <Text
                  fw={500}
                  c={affirmEnabled && affirmCapable ? undefined : "dimmed"}
                >
                  Affirm
                </Text>
              </Group>
            </Tooltip>
            <AffirmInfoIcon />
            {!affirmCapable && (
              <Text size="xs" c="dimmed">
                {AFFIRM_STRIPE_PREREQ_MESSAGE}
              </Text>
            )}
          </Group>
        ) : (
          <Group gap="xs" wrap="nowrap">
            {affirmCapable ? (
              <>
                <Tooltip label={AFFIRM_TOOLTIP_MESSAGE} withArrow>
                  <Box>
                    <Checkbox
                      label={<Text fw={500}>Affirm</Text>}
                      checked={draftAffirmEnabled}
                      disabled={isSaving}
                      onChange={() => setDraftAffirmEnabled((prev) => !prev)}
                    />
                  </Box>
                </Tooltip>
                <AffirmInfoIcon />
              </>
            ) : (
              <>
                <Tooltip label={AFFIRM_STRIPE_PREREQ_MESSAGE} withArrow>
                  <Box>
                    <Checkbox
                      label={<Text fw={500}>Affirm</Text>}
                      checked={affirmEnabled}
                      disabled
                      onChange={() => {}}
                    />
                  </Box>
                </Tooltip>
                <AffirmInfoIcon />
                <Text size="xs" c="dimmed">
                  {AFFIRM_STRIPE_PREREQ_MESSAGE}
                </Text>
              </>
            )}
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

// --- Row presentational components -----------------------------------------

const ViewRow: React.FC<{ row: DraftRow }> = ({ row }) => (
  <Group gap="xs" wrap="nowrap">
    {row.isSelected ? (
      <IconCheck size={16} color="var(--brand-teal)" />
    ) : (
      <Box w={16} />
    )}
    <Text fw={500} c={row.isSelected ? undefined : "dimmed"}>
      {row.name}
    </Text>
    {row.isDefault && (
      <Text size="xs" c="dimmed">
        (default)
      </Text>
    )}
  </Group>
);

interface EditRowProps {
  row: DraftRow;
  disabled: boolean;
  disableToggleOn: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

const EditRow: React.FC<EditRowProps> = ({
  row,
  disabled,
  disableToggleOn,
  onToggle,
  onDelete,
}) => (
  <Group justify="space-between" gap="sm" wrap="nowrap">
    <Checkbox
      label={
        <Group gap={6} wrap="nowrap">
          <Text fw={500}>{row.name}</Text>
          {row.isDefault && (
            <Text size="xs" c="dimmed">
              (default)
            </Text>
          )}
        </Group>
      }
      checked={row.isSelected}
      disabled={disabled || disableToggleOn}
      onChange={onToggle}
    />
    {!row.isDefault && (
      <Tooltip label="Remove" withArrow>
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={onDelete}
          disabled={disabled}
          aria-label={`Remove ${row.name}`}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Tooltip>
    )}
  </Group>
);

export default OffPlatformPaymentsSettings;
